"use client"

import type React from "react"
import { useEffect, useMemo, useState } from "react"
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaUserGraduate,
  FaEye,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaRupeeSign,
  FaFileExcel,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaUpload,
  FaCopy,
} from "react-icons/fa"
import { FiFilter, FiX, FiRefreshCw, FiSearch as FiSearchIcon, FiUser, FiBookOpen, FiCalendar, FiExternalLink } from "react-icons/fi"
import { Pagination as PaginationComponent } from "../../../utils/pagination.utils"
import type { SelectOption } from "../../../utils/select-with-search"
import { SearchableSelect } from "../../../utils/select-with-search"
import { TrainingRequest, Pagination } from "../types"
import { useAuth } from "../../../provider/auth.provider"
import { ExcelDownload } from "../../../utils/excel.utils"
import toast from "react-hot-toast"
import api from "../../../libs/axios"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

export interface RequestFilters {
  [key: string]: string | undefined
  search?: string
  request_id?: string
  candidate_name?: string
  candidate_phone?: string
  candidate_location?: string
  association_type?: string
  status?: string
  designation_id?: string
  designation_label?: string
  training_type_id?: string
  training_type_label?: string
  training_institute_id?: string
  training_institute_label?: string
  project_id?: string
  project_label?: string
  salary_min?: string
  salary_max?: string
  planned_date_from?: string
  planned_date_to?: string
  created_from?: string
  created_to?: string
  deduction_applicable?: string
}

type TrainingRequestExportRow = Record<string, string | number | boolean | null>

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: RequestFilters
  onFiltersChange: (filters: RequestFilters) => void
  onApply: () => void
  onReset: () => void
  trainingTypeOptions: SelectOption[]
  designationOptions: SelectOption[]
  projectOptions: SelectOption[]
  trainingInstituteOptions: SelectOption[]
  requestOptions: SelectOption[]
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  trainingTypeOptions,
  designationOptions,
  projectOptions,
  trainingInstituteOptions,
  requestOptions,
}) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [selectedDesignation, setSelectedDesignation] = useState<{ value: string | number; label: string } | null>(null)
  const [selectedProject, setSelectedProject] = useState<{ value: string | number; label: string } | null>(null)
  const [selectedRequest, setSelectedRequest] = useState<SelectOption | null>(null)

  const designationSelectOptions = useMemo(() => (
    designationOptions.map((option) => ({ value: option.value, label: option.label }))
  ), [designationOptions])

  const projectSelectOptions = useMemo(() => (
    projectOptions.map((option) => ({ value: option.value, label: option.label }))
  ), [projectOptions])

  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic")
    }
  }, [isOpen])

  useEffect(() => {
    if (filters.designation_id) {
      const existingOption = designationSelectOptions.find((option) => String(option.value) === filters.designation_id)
      setSelectedDesignation({
        value: filters.designation_id,
        label: filters.designation_label || existingOption?.label || `Designation ${filters.designation_id}`,
      })
    } else {
      setSelectedDesignation(null)
    }
  }, [filters.designation_id, filters.designation_label, designationSelectOptions])

  useEffect(() => {
    if (filters.project_id) {
      const existingOption = projectSelectOptions.find((option) => String(option.value) === filters.project_id)
      setSelectedProject({
        value: filters.project_id,
        label: filters.project_label || existingOption?.label || `Project ${filters.project_id}`,
      })
    } else {
      setSelectedProject(null)
    }
  }, [filters.project_id, filters.project_label, projectSelectOptions])

  useEffect(() => {
    if (!filters.request_id) {
      setSelectedRequest(null)
      return
    }
    const existingOption = requestOptions.find((option) => String(option.value) === filters.request_id)
    if (existingOption) {
      setSelectedRequest(existingOption)
      return
    }
    setSelectedRequest({ id: 0, label: filters.request_id, value: filters.request_id })
  }, [filters.request_id, requestOptions])

  if (!isOpen) return null

  const handleInputChange = (field: keyof RequestFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const handleSelectChange = (
    field: keyof RequestFilters,
    labelField: keyof RequestFilters | null,
    value: string,
    label?: string,
  ) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
      ...(labelField ? { [labelField]: value ? label : undefined } : {}),
    })
  }

  const handleDesignationSelect = (option: { value: string | number; label: string } | null) => {
    setSelectedDesignation(option)
    onFiltersChange({
      ...filters,
      designation_id: option ? String(option.value) : undefined,
      designation_label: option ? option.label : undefined,
    })
  }

  const handleProjectSelect = (option: { value: string | number; label: string } | null) => {
    setSelectedProject(option)
    onFiltersChange({
      ...filters,
      project_id: option ? String(option.value) : undefined,
      project_label: option ? option.label : undefined,
    })
  }

  const handleRequestSelect = (option: SelectOption | null) => {
    setSelectedRequest(option)
    onFiltersChange({
      ...filters,
      request_id: option ? String(option.value) : undefined,
    })
  }

  const tabs = [
    { id: "basic", label: "Basic Info", icon: FiSearchIcon },
    { id: "candidate", label: "Candidate", icon: FiUser },
    { id: "training", label: "Training", icon: FiBookOpen },
    { id: "amounts", label: "Amounts", icon: FaRupeeSign },
    { id: "dates", label: "Dates", icon: FiCalendar },
  ]

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
    { value: "hr_verified", label: "HR Verified" },
    { value: "scheduled", label: "Scheduled" },
    { value: "paid", label: "Paid" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600 bg-white"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search requests..."
                  value={filters.search || ""}
                  onChange={(e) => handleInputChange("search", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Request</label>
                {/* <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Request ID"
                  value={filters.request_id || ""}
                  onChange={(e) => handleInputChange("request_id", e.target.value)}
                /> */}
                <SearchableSelect
                  value={selectedRequest}
                  onChange={handleRequestSelect}
                  options={requestOptions}
                  placeholder="Search requests"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.status || ""}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.association_type || ""}
                  onChange={(e) => handleInputChange("association_type", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="onrole">On-Role</option>
                  <option value="offrole">Off-Role</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === "candidate" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Candidate name"
                  value={filters.candidate_name || ""}
                  onChange={(e) => handleInputChange("candidate_name", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Phone</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Phone number"
                  value={filters.candidate_phone || ""}
                  onChange={(e) => handleInputChange("candidate_phone", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Candidate location"
                  value={filters.candidate_location || ""}
                  onChange={(e) => handleInputChange("candidate_location", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                <SearchableSelect
                  value={selectedDesignation}
                  onChange={handleDesignationSelect}
                  options={designationSelectOptions}
                  placeholder="Search designations"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
            </div>
          )}

          {activeTab === "training" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.training_type_id || ""}
                  onChange={(e) =>
                    handleSelectChange(
                      "training_type_id",
                      "training_type_label",
                      e.target.value,
                      e.target.selectedOptions[0]?.text,
                    )
                  }
                >
                  <option value="">All Training Types</option>
                  {trainingTypeOptions.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Institute</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.training_institute_id || ""}
                  onChange={(e) =>
                    handleSelectChange(
                      "training_institute_id",
                      "training_institute_label",
                      e.target.value,
                      e.target.selectedOptions[0]?.text,
                    )
                  }
                >
                  <option value="">All Institutes</option>
                  {trainingInstituteOptions.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                <SearchableSelect
                  value={selectedProject}
                  onChange={handleProjectSelect}
                  options={projectSelectOptions}
                  placeholder="Search projects"
                  className="w-full"
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deduction Applicable</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.deduction_applicable || ""}
                  onChange={(e) => handleInputChange("deduction_applicable", e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === "amounts" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Salary</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Minimum salary"
                  value={filters.salary_min || ""}
                  onChange={(e) => handleInputChange("salary_min", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Salary</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Maximum salary"
                  value={filters.salary_max || ""}
                  onChange={(e) => handleInputChange("salary_max", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Planned Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.planned_date_from || ""}
                  onChange={(e) => handleInputChange("planned_date_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Planned Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.planned_date_to || ""}
                  onChange={(e) => handleInputChange("planned_date_to", e.target.value)}
                />
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ""}
                  onChange={(e) => handleInputChange("created_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ""}
                  onChange={(e) => handleInputChange("created_to", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const UserProjectsCell: React.FC<{ projects: string[] }> = ({ projects }) => {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? projects : projects.slice(0, 2)
  return (
    <div className="flex flex-wrap gap-1 items-center max-w-[200px]">
      {visible.map((name, idx) => (
        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">{name}</span>
      ))}
      {!showAll && projects.length > 2 && (
        <button onClick={() => setShowAll(true)} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-600 border border-dashed border-gray-300 hover:bg-blue-50 hover:text-blue-700 transition-colors">
          +{projects.length - 2} more
        </button>
      )}
    </div>
  )
}

interface RequestsListProps {
  requests: TrainingRequest[]
  pagination: Pagination
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onAssociationFilter: (association: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdd: () => void
  onEdit: (request: TrainingRequest) => void
  onDuplicate: (request: TrainingRequest) => void
  onView: (request: TrainingRequest) => void
  onCancel: (request: TrainingRequest) => void
  loading?: boolean
  sortConfig?: {
    field: string
    direction: "ASC" | "DESC"
  }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  onAdvancedFilter?: (filters: RequestFilters) => void
  advancedFilters?: RequestFilters
  trainingTypeOptions?: SelectOption[]
  designationOptions?: SelectOption[]
  projectOptions?: SelectOption[]
  trainingInstituteOptions?: SelectOption[]
  requestOptions?: SelectOption[]
}

export const RequestsList: React.FC<RequestsListProps> = ({
  requests,
  pagination,
  onSearch,
  onStatusFilter,
  onAssociationFilter,
  onPageChange,
  onLimitChange,
  onAdd,
  onEdit,
  onDuplicate,
  onView,
  onCancel,
  loading = false,
  sortConfig,
  onSort,
  onAdvancedFilter,
  advancedFilters = {},
  trainingTypeOptions = [],
  designationOptions = [],
  projectOptions = [],
  trainingInstituteOptions = [],
  requestOptions = [],
}) => {
  const { hasPermission } = useAuth()
  const canViewRequest = hasPermission("TrainingRequest", "view")
  const canCreateRequest = hasPermission("TrainingRequest", "create")
  const canEditRequest = hasPermission("TrainingRequest", "edit")
  const canDuplicateRequest = hasPermission("TrainingRequest", "duplicate")
  const canCancelRequest = hasPermission("TrainingRequest", "cancel")
  const canUpdateDeduction = hasPermission("TrainingCertification", "update_deduction")

  const [searchTerm, setSearchTerm] = useState("")
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)
  const [statusFilter, setStatusFilter] = useState("")
  const [associationFilter, setAssociationFilter] = useState("")
  const [filters, setFilters] = useState<RequestFilters>(advancedFilters)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<TrainingRequestExportRow[]>([])
  const [deductionModal, setDeductionModal] = useState<{ show: boolean; certId: number; request?: TrainingRequest }>({ show: false, certId: 0 })
  const [deductionAction, setDeductionAction] = useState<"deducted" | "waived" | "pending" | "payment_received">("deducted")
  const [deductionAmount, setDeductionAmount] = useState("")
  const [deductionDate, setDeductionDate] = useState("")
  const [deductionRemarks, setDeductionRemarks] = useState("")
  const [paymentReceivedAmount, setPaymentReceivedAmount] = useState("")
  const [paymentReceivedDate, setPaymentReceivedDate] = useState("")
  const [deductionLoading, setDeductionLoading] = useState(false)
  const [deductionDocs, setDeductionDocs] = useState<any[]>([])
  const [deductionDocsLoading, setDeductionDocsLoading] = useState(false)
  const [deductionNewFiles, setDeductionNewFiles] = useState<File[]>([])
  const [deductionDocType, setDeductionDocType] = useState("deduction_slip")

  useEffect(() => {
    if (!deductionModal.show || !deductionModal.certId) {
      setDeductionDocs([])
      setDeductionNewFiles([])
      setDeductionDocType("deduction_slip")
      return
    }
    setDeductionDocsLoading(true)
    api.get(`/training/certifications/${deductionModal.certId}/documents`)
      .then(res => { if (res.data.success) setDeductionDocs(res.data.data?.documents || []) })
      .catch(() => setDeductionDocs([]))
      .finally(() => setDeductionDocsLoading(false))
  }, [deductionModal.certId, deductionModal.show])

  useEffect(() => {
    setFilters(advancedFilters)
  }, [advancedFilters])

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters)
      .filter(([key, value]) => !key.endsWith("_label") && value)
      .length

  const handleFiltersChange = (updatedFilters: RequestFilters) => {
    setFilters(updatedFilters)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
    setIsFilterModalOpen(false)
  }

  const handleDeductionUpdate = async () => {
    if (!deductionModal.certId) return
    setDeductionLoading(true)
    try {
      const data: any = { deduction_status: deductionAction }
      if (deductionAction === "deducted") {
        data.deduction_amount = Number(deductionAmount) || 0
        if (deductionDate) data.deduction_date = deductionDate
      } else if ((deductionAction === "waived" || deductionAction === "pending") && deductionAmount !== "") {
        // The amount still to be booked - kept so it stays visible until the deduction is booked.
        data.deduction_amount = Number(deductionAmount) || 0
      }
      if (deductionAction === "payment_received") {
        if (paymentReceivedAmount) data.payment_received_amount = Number(paymentReceivedAmount)
        if (paymentReceivedDate) data.payment_received_date = paymentReceivedDate
      }
      if (deductionRemarks.trim()) data.deduction_remarks = deductionRemarks.trim()
      await api.patch(`/training/certifications/${deductionModal.certId}/deduction`, data)

      for (const file of deductionNewFiles) {
        const fd = new FormData()
        fd.append("document", file)
        fd.append("document_type", deductionDocType)
        fd.append("document_name", file.name)
        await api.post(`/training/certifications/${deductionModal.certId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      }

      toast.success("Deduction updated successfully")
      setDeductionModal({ show: false, certId: 0 })
      setDeductionAmount(""); setDeductionDate(""); setDeductionRemarks(""); setDeductionAction("deducted")
      setPaymentReceivedAmount(""); setPaymentReceivedDate("")
      setDeductionNewFiles([]); setDeductionDocType("deduction_slip")
      onAdvancedFilter?.(filters)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update deduction")
    } finally {
      setDeductionLoading(false)
    }
  }

  const handleDeleteDeductionDoc = async (docId: number) => {
    try {
      await api.delete(`/training/certifications/documents/${docId}`)
      setDeductionDocs(prev => prev.filter((d: any) => d.id !== docId))
      toast.success("Document removed")
    } catch {
      toast.error("Failed to remove document")
    }
  }

  const handleDeductionFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDeductionNewFiles(prev => [...prev, ...Array.from(e.target.files || [])])
      e.target.value = ""
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  }

  const formatDateOnly = (dateString?: string | null) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  }

  const formatDateTimeValue = (value?: string | null) => {
    if (!value) return "-"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatCurrency = (value?: number | string | null) => {
    if (value === undefined || value === null || value === "") return "-"
    const numericValue = typeof value === "number" ? value : Number(value)
    if (Number.isNaN(numericValue)) return "-"
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(numericValue)
  }

  const formatBooleanLabel = (value?: boolean) => (value ? "Yes" : "No")

  const getAssociationLabel = (type?: string) => {
    if (!type) return "-"
    const normalized = type.toLowerCase().replace(/-/g, "")
    return normalized === "onrole" ? "On-Role" : "Off-Role"
  }

  const getUserName = (user?: { first_name?: string; last_name?: string } | null) => {
    if (!user) return "-"
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || "-"
  }

  const formatDeductionPeriod = (period?: string | null) => {
    if (!period) return "-"
    return period
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      rejected: "bg-red-100 text-red-800",
      hr_verified: "bg-blue-100 text-blue-800",
      scheduled: "bg-purple-100 text-purple-800",
      paid: "bg-teal-100 text-teal-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    }
    const labels: Record<string, string> = {
      pending: "Pending",
      rejected: "Rejected",
      hr_verified: "HR Verified",
      scheduled: "Scheduled",
      paid: "Paid",
      completed: "Completed",
      cancelled: "Cancelled",
    }
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    )
  }

  const getTrainingStageMeta = (request: TrainingRequest) => {
    const hasPaidPayment = Boolean(request.payment_records?.some((record) => record.training_payment?.payment_status === "paid"))

    if (request.training_stage === "payment_done" || hasPaidPayment) {
      return { label: "Payment Done", className: "bg-green-100 text-green-800" }
    }

    if (request.training_stage === "training_certified" || request.certification) {
      return { label: "Training Certified", className: "bg-blue-100 text-blue-800" }
    }

    return { label: "Request Generated", className: "bg-gray-100 text-gray-700" }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction = sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />
    }
    return sortConfig.direction === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-600" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-600" />
    )
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number | boolean> = { page, limit }

    if (searchTerm.trim()) params.search = searchTerm.trim()
    if (statusFilter) params.status = statusFilter
    if (associationFilter) params.association_type = associationFilter

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (key.endsWith("_label")) return
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value
      }
    })

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const deductionStageLabel = (ds?: string) => {
    if (!ds) return "-"
    const labels: Record<string, string> = { deducted: "Deduction Booked", payment_received: "Payment Received", waived: "Deduction to be Booked", pending: "Deduction Pending" }
    return labels[ds] || ds
  }

  const mapRequestForExport = (request: TrainingRequest): TrainingRequestExportRow => {
    const vendorName = request.vendor?.vendor_full_name || request.vendor?.vendor_name || request.vendor?.vendor_short_name || "-"
    const cert = request.certification

    return {
      "Request ID": request.request_id,
      "Candidate Name": request.candidate_name,
      "Candidate Designation": request.candidate_designation || "-",
      "Candidate Phone": request.candidate_phone,
      "Alternate Phone": request.candidate_alternate_phone || "-",
      "Candidate Location": request.candidate_location || "-",
      "Association Type": getAssociationLabel(request.association_type),
      "Vendor Name": vendorName,
      "Project Name": request.project?.project_name || "-",
      "Project Short Name": request.project?.project_short_name || "-",
      "Training Type": request.training_type?.training_name || "-",
      "Training Institute": request.training_institute?.institute_name || "-",
      "Existing User": request.user_id ? "Yes" : "No",
      "Deduction Applicable": formatBooleanLabel(request.deduction_applicable),
      "Deduction Period": formatDeductionPeriod(request.deduction_period),
      "Deduction Remarks": request.deduction_remarks || "-",
      "Deduction Stage": deductionStageLabel(cert?.deduction_status),
      "Deduction Amount": cert?.deduction_amount != null ? formatCurrency(cert.deduction_amount) : "-",
      "Deduction Booked Date": formatDateOnly(cert?.deduction_date),
      "Deduction Booked Remarks": cert?.deduction_remarks || "-",
      "Training Date": formatDateOnly(cert?.training_date),
      "Expiry Date": formatDateOnly(cert?.expiry_date),
      "Salary Offered": formatCurrency(request.salary_offered ?? null),
      "Training Stage": getTrainingStageMeta(request).label,
      Status: request.status || "-",
      "Planned Training Date": formatDateOnly(request.planned_training_date),
      "Planned Location": request.planned_location || "-",
      "HR Verified By": getUserName(request.hr_verifier),
      "HR Verified At": formatDateTimeValue(request.hr_verified_at),
      "Scheduled By": getUserName(request.scheduler),
      "Scheduled At": formatDateTimeValue(request.scheduled_at),
      "Created By": getUserName(request.creator),
      "Creator Email": request.creator?.email || "-",
      "Created At": formatDateTimeValue(request.created_at),
      "Updated At": formatDateTimeValue(request.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (exporting) return

    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allRequests: TrainingRequest[] = []

      while (hasMore) {
        const params = buildExportParams(page, pageSize)
        const response = await api.get("/training/requests/list", { params })

        if (!response.data?.success) {
          throw new Error(response.data?.message || "Failed to fetch training requests")
        }

        const data = response.data.data || {}
        const fetched: TrainingRequest[] = data.training_requests || []
        const paginationInfo = data.pagination

        allRequests.push(...fetched)

        const nextPageAvailable = paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false
        } else {
          page += 1
        }
      }

      if (!allRequests.length) {
        toast.error("No training requests found for export")
        return
      }

      setExportData(allRequests.map(mapRequestForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error exporting training requests:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare export data")
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Training Requests</h2>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              {exporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export Excel
                </>
              )}
            </button>
            {canCreateRequest && (
              <button onClick={onAdd} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm">
                <FaPlus className="w-4 h-4" /> New Request
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input type="text" placeholder="Search by name, phone, ID..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); debouncedOnSearch(e.target.value) }} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="w-full sm:w-40">
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); onStatusFilter(e.target.value) }} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="hr_verified">HR Verified</option>
                <option value="scheduled">Scheduled</option>
                <option value="paid">Paid</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {/* <div className="w-full sm:w-40">
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={associationFilter} onChange={(e) => { setAssociationFilter(e.target.value); onAssociationFilter(e.target.value) }} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                <option value="">All</option>
                <option value="inhouse">In-house</option>
                <option value="vendor">Vendor</option>
              </select>
            </div> */}
            <div className="w-full sm:w-auto flex items-end">
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(true)}
                className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${
                  getActiveFiltersCount() > 0 ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("request_id")}>
                  <span>Request ID</span>
                  {getSortIcon("request_id")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("candidate_name")}>
                  <span>Candidate</span>
                  {getSortIcon("candidate_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("candidate_phone")}>
                  <span>Phone</span>
                  {getSortIcon("candidate_phone")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("candidate_location")}>
                  <span>Location</span>
                  {getSortIcon("candidate_location")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("association_type")}>
                  <span>Association</span>
                  {getSortIcon("association_type")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Vendor Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Training Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Institute</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Planned Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Training Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Training Expiry Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[110px]">Deduction Period</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Deduction Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("status")}>
                  <span>Status</span>
                  {getSortIcon("status")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Training Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Linked to User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Requestor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Project</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">User Projects</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Request Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Payment Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" className="flex items-center gap-1" onClick={() => handleColumnSort("created_at")}>
                  <span>Created At</span>
                  {getSortIcon("created_at")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={23} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div><span className="text-sm text-gray-600">Loading requests...</span></div></td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={23} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3"><FaUserGraduate className="w-6 h-6 text-gray-400" /></div><p className="text-sm text-gray-500">No training requests found</p></div></td></tr>
            ) : (
              requests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {canViewRequest && (
                        <button onClick={() => onView(request)} className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-50 rounded" title="View">
                          <FaEye className="w-4 h-4" />
                        </button>
                      )}
                      {canEditRequest && (
                        <button onClick={() => onEdit(request)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded" title="Edit">
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {canDuplicateRequest && (
                        <button onClick={() => onDuplicate(request)} className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded" title="Duplicate">
                          <FaCopy className="w-4 h-4" />
                        </button>
                      )}
                      {request.status === "pending" && canCancelRequest && (
                        <button onClick={() => onCancel(request)} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded" title="Cancel">
                          <FiX className="w-4 h-4" />
                        </button>
                      )}
                      {request.certification && request.deduction_applicable && canUpdateDeduction && (() => {
                        const ds = (request.certification as any).deduction_status
                        const isLocked = ds === "deducted" || ds === "payment_received"
                        return (
                          <button
                            onClick={() => {
                              if (isLocked) return
                              setDeductionModal({ show: true, certId: (request.certification as any).id, request })
                              setDeductionAction(ds || "deducted")
                              setDeductionAmount((request.certification as any).deduction_amount?.toString() || "")
                              setDeductionDate((request.certification as any).deduction_date?.split("T")[0] || "")
                              setDeductionRemarks((request.certification as any).deduction_remarks || "")
                            }}
                            className={`p-2 rounded ${isLocked ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50'}`}
                            title={isLocked ? "Deduction is locked (Deduction Booked or Payment Received)" : "Update Deduction"}
                            disabled={isLocked}
                          >
                            <FaRupeeSign className="w-4 h-4" />
                          </button>
                        )
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-700">{request.request_id}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <p className="text-sm font-semibold text-gray-900">{request.candidate_name}</p>
                    <p className="text-xs text-gray-500">{request.candidate_designation}</p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.candidate_phone}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.candidate_location}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${(request.association_type || '').toLowerCase().replace(/-/g, '') === 'onrole' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                      {(request.association_type || '').toLowerCase().replace(/-/g, '') === 'onrole' ? 'On-Role' : 'Off-Role'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.vendor?.vendor_full_name || request.vendor?.vendor_name || request.vendor?.vendor_short_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.training_type?.training_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.training_institute?.institute_name || "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.planned_training_date ? new Date(request.planned_training_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.certification?.training_date ? new Date(request.certification.training_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{request.certification?.expiry_date ? new Date(request.certification.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{request.deduction_period ? request.deduction_period.replace(/_/g, " ") : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {request.certification ? (() => {
                      const ds = request.certification.deduction_status
                      const labels: Record<string, string> = { deducted: "Deduction Booked", payment_received: "Payment Received", waived: "Deduction to be Booked", pending: "Deduction Pending" }
                      const colors: Record<string, string> = { deducted: "bg-green-100 text-green-800", payment_received: "bg-emerald-100 text-emerald-800", waived: "bg-blue-100 text-blue-800", pending: "bg-yellow-100 text-yellow-800" }
                      const amount = request.certification.deduction_amount
                      return (
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[ds] || 'bg-gray-100 text-gray-800'}`}>{labels[ds] || ds}</span>
                          {amount != null && <span className="text-xs font-medium text-gray-700">₹{Number(amount).toLocaleString("en-IN")}</span>}
                        </div>
                      )
                    })() : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(request.status)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {(() => {
                      const trainingStage = getTrainingStageMeta(request)
                      return (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${trainingStage.className}`}>
                          {trainingStage.label}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${(request.user_id || request.certification?.linked_to_permanent_user) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {(request.user_id || request.certification?.linked_to_permanent_user) ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {request.creator ? `${request.creator.first_name} ${request.creator.last_name}` : "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                    {request.project?.project_name || <span className="text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4">
                    {(() => {
                      const projects = request.linked_user?.user_projects?.map(up => up.project?.project_name).filter(Boolean) as string[] | undefined
                      if (!request.user_id || !projects?.length) return <span className="text-xs text-gray-400">—</span>
                      return (
                        <UserProjectsCell projects={projects} />
                      )
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${request.user_id ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                      {request.user_id ? 'Existing' : 'New'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {(() => {
                      const paymentStatuses = request.payment_records?.map(pr => pr.training_payment?.payment_status).filter(Boolean) as string[] | undefined
                      if (!paymentStatuses?.length) return <span className="text-xs text-gray-400">—</span>
                      const hasPaid = paymentStatuses.includes("paid")
                      return (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${hasPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {hasPaid ? 'Paid' : 'Pending'}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(request.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationComponent pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        trainingTypeOptions={trainingTypeOptions}
        designationOptions={designationOptions}
        projectOptions={projectOptions}
        trainingInstituteOptions={trainingInstituteOptions}
        requestOptions={requestOptions}
      />
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="training_requests"
        title="Export Training Requests"
        description="Download comprehensive training request details"
      />
      

      {/* Update Deduction Offcanvas */}
      {deductionModal.show && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !deductionLoading && setDeductionModal({ show: false, certId: 0 })} />
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="font-semibold text-gray-800">Update Deduction</h3>
                {deductionModal.request && <p className="text-xs text-gray-500 mt-0.5">{deductionModal.request.candidate_name} · {deductionModal.request.request_id}</p>}
              </div>
              <button onClick={() => !deductionLoading && setDeductionModal({ show: false, certId: 0 })} className="p-1.5 hover:bg-gray-100 rounded" disabled={deductionLoading}><FaTimes className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {deductionModal.request?.deduction_applicable && (
                <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm space-y-1">
                  <p className="font-medium text-orange-800">Request Deduction Details</p>
                  <p className="text-orange-700">Period: <span className="font-medium capitalize">{deductionModal.request.deduction_period?.replace(/_/g, " ") || "—"}</span></p>
                  {deductionModal.request.deduction_remarks && <p className="text-orange-700">Remarks: <span className="font-medium">{deductionModal.request.deduction_remarks}</span></p>}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Status</label>
                <select value={deductionAction} onChange={e => setDeductionAction(e.target.value as any)} disabled={deductionLoading} className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                  <option value="deducted">Deduction Booked</option>
                  <option value="payment_received">Payment Received</option>
                  <option value="waived">Deduction to be Booked</option>
                  <option value="pending">Deduction Pending</option>
                </select>
              </div>
              {(deductionAction === "deducted" || deductionAction === "waived" || deductionAction === "pending") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Amount{deductionAction === "deducted" ? " *" : ""}</label>
                  <input type="number" value={deductionAmount} onChange={e => setDeductionAmount(e.target.value)} disabled={deductionLoading} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Amount" step="0.01" />
                </div>
              )}
              {deductionAction === "deducted" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Date</label>
                    <input type="date" value={deductionDate} onChange={e => setDeductionDate(e.target.value)} disabled={deductionLoading} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                  </div>
                </>
              )}
              {deductionAction === "payment_received" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                    <input type="number" value={paymentReceivedAmount} onChange={e => setPaymentReceivedAmount(e.target.value)} disabled={deductionLoading} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Amount" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                    <input type="date" value={paymentReceivedDate} onChange={e => setPaymentReceivedDate(e.target.value)} disabled={deductionLoading} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea value={deductionRemarks} onChange={e => setDeductionRemarks(e.target.value)} disabled={deductionLoading} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Optional remarks..." />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Documents</label>
                {deductionDocsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <FaSpinner className="animate-spin w-3.5 h-3.5" /> Loading documents...
                  </div>
                ) : deductionDocs.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    {deductionDocs.map((doc: any) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded text-sm">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-700 capitalize">{doc.document_type?.replace(/_/g, " ") || "Document"}</p>
                          <p className="text-xs text-gray-500 truncate">{doc.document_name}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <a href={doc.document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <FiExternalLink className="w-3 h-3" /> View
                          </a>
                          <button type="button" onClick={() => handleDeleteDeductionDoc(doc.id)} disabled={deductionLoading} className="text-red-500 hover:text-red-700 disabled:opacity-40">
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 mb-2">
                  <select
                    value={deductionDocType}
                    onChange={e => setDeductionDocType(e.target.value)}
                    disabled={deductionLoading}
                    className="w-36 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white flex-shrink-0"
                  >
                    <option value="deduction_slip">Deduction Slip</option>
                    <option value="letter">Letter</option>
                    <option value="salary_slip">Salary Slip</option>
                    <option value="proof">Proof</option>
                    <option value="other">Other</option>
                  </select>
                  <label className="flex-1 cursor-pointer flex items-center justify-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors">
                    <FaUpload className="w-3 h-3" />
                    Upload Documents
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      onChange={handleDeductionFileSelect}
                      disabled={deductionLoading}
                    />
                  </label>
                </div>

                {deductionNewFiles.length > 0 && (
                  <div className="space-y-1">
                    {deductionNewFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-blue-50 border border-blue-200 rounded text-xs">
                        <span className="text-blue-900 truncate flex-1">{file.name}</span>
                        <button type="button" onClick={() => setDeductionNewFiles(prev => prev.filter((_, i) => i !== idx))} disabled={deductionLoading} className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0">
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t p-5 flex gap-3">
              <button onClick={handleDeductionUpdate} disabled={deductionLoading || (deductionAction === "deducted" && !deductionAmount)}
                className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                {deductionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaCheck className="w-4 h-4" />} Update Deduction
              </button>
              <button onClick={() => { setDeductionModal({ show: false, certId: 0 }); setDeductionAmount(""); setDeductionDate(""); setDeductionRemarks("") }} disabled={deductionLoading}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
